import { useEffect, useState } from "react";
import { getDefaultCardState } from "../functions/workflowFunctions";
import { CardState } from "../routes/basic/workflow";

interface CardsState {
  [key: string]: CardState;
}

export const useCardState = (data: any, existingStaffCount: number) => {
  const [cardsState, setCardsState] = useState<Record<string, any>>({});

  const initialCardsState: CardsState = {
    staff: getDefaultCardState(),
    contact: getDefaultCardState(),
    matter: getDefaultCardState(),
    matterDetail: getDefaultCardState(),
    matterArchive: getDefaultCardState(),
    matterType: getDefaultCardState(false, true),
    matterTag: getDefaultCardState(),
    matterOpeningBalance: getDefaultCardState(),
    matterRelationship: getDefaultCardState(),
    activity: getDefaultCardState(),
    unbilledTimeAndFee: getDefaultCardState(),
    unbilledExpense: getDefaultCardState(),
    invoice: getDefaultCardState(),
    invoiceItem: getDefaultCardState(false, false),
    memo: getDefaultCardState(),
    task: getDefaultCardState(),
    event: getDefaultCardState(),
    safeCustodyPacket: getDefaultCardState(),
    safeCustodyDocument: getDefaultCardState(false, true),
  };

  useEffect(() => {
    const imports = data?.imports || [];

    const updatedCardsState = imports.reduce(
      (
        acc: {
          [x: string]: {
            hasUploaded: any;
            hasWarnings: boolean;
            hasErrors: boolean;
            uploadWarningCount: any;
            uploadErrorCount: any;
            uploadTotalRecordCount: any;
            isSuccess: boolean;
            isReady: boolean;
            isDisabled: boolean;
            needsRevalidation: boolean;
          };
        },
        importData: {
          importType: any;
          hasUploaded: any;
          uploadErrorCount: any;
          uploadWarningCount: any;
          uploadTotalRecordCount: any;
        },
      ) => {
        const {
          importType,
          hasUploaded,
          uploadErrorCount,
          uploadWarningCount,
          uploadTotalRecordCount,
        } = importData;

        const hasWarnings = uploadWarningCount > 0;
        const hasErrors = uploadErrorCount > 0;
        const needsRevalidation = uploadErrorCount === -1;
        const isSuccess = !hasErrors && !!hasUploaded && !needsRevalidation;

        // Safely handle unknown importTypes not present in initialCardsState
        // If acc[importType] is undefined (unknown type from backend), initialise it
        if (!acc[importType]) {
          console.warn(`Unexpected importType: ${importType}`);
          // Initialize with default card state
          acc[importType] = getDefaultCardState();
        }

        // Update card states based on import data
        acc[importType] = {
          ...acc[importType], // preserve isReady/isDisabled from initial
          hasUploaded,
          hasWarnings,
          hasErrors,
          uploadWarningCount,
          uploadErrorCount,
          uploadTotalRecordCount,
          isSuccess,
          isReady:
            acc[importType].isReady ?? (!hasUploaded && !needsRevalidation),
          isDisabled: acc[importType].isDisabled ?? false,
          needsRevalidation,
        };

        return acc;
      },
      { ...initialCardsState },
    );

    // Apply logic for when "Safe Custody Document"
    // cards should be marked is ready
    updatedCardsState.safeCustodyDocument.isReady =
      updatedCardsState.safeCustodyPacket.isSuccess;
    // Apply logic for when "Safe Custody Document" card should not be disabled if it needs revalidation
    if (updatedCardsState.safeCustodyDocument.needsRevalidation) {
      updatedCardsState.safeCustodyDocument.isDisabled = false;
    } else {
      updatedCardsState.safeCustodyDocument.isDisabled =
        !updatedCardsState.safeCustodyPacket.isSuccess &&
        !updatedCardsState.safeCustodyDocument.hasUploaded;
    }

    // Update 'matterType.isDisabled' based on 'matter.needsRevalidation' or 'matter.isSuccess' status
    if (
      updatedCardsState.matter.needsRevalidation ||
      updatedCardsState.matter.isSuccess
    ) {
      updatedCardsState.matterType.isDisabled = false;
    } else {
      updatedCardsState.matterType.isDisabled =
        !updatedCardsState.matter.isSuccess;
    }

    setCardsState(updatedCardsState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, existingStaffCount]);

  return cardsState;
};
